import React, { ReactNode, useState } from "react"
import { QueueContext } from "./dlQueueContext"
import { VideoType } from "../api/video"
import { Box, Grid, Typography } from "@mui/material"
import { httpClient } from "../http"
import { useTheme } from "@mui/material"

import { ReactComponent as CrossIcon } from "../../assets/svg/cross.svg"
import { ReactComponent as RetryIcon } from "../../assets/svg/retry.svg"

interface DownloadQueueElement {
  id: number
  video: VideoType
  progress: number
  status: "waiting" | "downloading" | "completed" | "cancelled" | "error"
  swiped: boolean
  hidden: boolean
  abortController: AbortController
}

export const DLQueueProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [downloadQueue, setDownloadQueue] = useState<DownloadQueueElement[]>([])
  const [lastId, setLastId] = useState(0)

  const add = async (video: VideoType) => {
    if (downloadQueue.find((dqe) => dqe.video.id === video.id && dqe.status === "downloading")) {
      return
    }
    const abortController = new AbortController()
    const dqeId = lastId
    setDownloadQueue((prev) => [
      ...prev,
      { id: dqeId, video, progress: 0, status: "downloading", hidden: false, swiped: true, abortController },
    ])
    setLastId((prev) => prev + 1)
    setTimeout(() => {
      setSwiped(dqeId, false)
    }, 10)
    try {
      const response = await httpClient.instance.get<Blob>(`/media/video/download/${video.id}/1080`, {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.floor((progressEvent.loaded * 100) / progressEvent.total)
            setDownloadQueue((prev) =>
              prev.map((dqe) => (dqe.id === dqeId ? { ...dqe, progress: percentCompleted } : dqe)),
            )
          }
        },
        signal: abortController.signal,
      })

      const blobUrl = URL.createObjectURL(response)
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = `${video.title}.mp4`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
      setDownloadQueue((prev) => prev.map((dqe) => (dqe.id === dqeId ? { ...dqe, status: "completed" } : dqe)))
      setTimeout(() => {
        hide(dqeId)
      }, 3000)
    } catch (error) {
      setDownloadQueue((prev) => prev.map((dqe) => (dqe.id === dqeId ? { ...dqe, status: "error" } : dqe)))
      setTimeout(() => {
        hide(dqeId)
      }, 10000)
      console.error("Download failed:", error)
    }
  }

  const cancel = (dqeId: number) => {
    setDownloadQueue((prev) => prev.map((dqe) => (dqe.id === dqeId ? { ...dqe, status: "cancelled" } : dqe)))
    const dqe = downloadQueue.find((dqe) => dqe.id === dqeId)
    if (dqe?.abortController) {
      dqe.abortController.abort()
    }
    hide(dqeId)
  }

  const retry = (dqeId: number) => {
    const dqe = downloadQueue.find((dqe) => dqe.id === dqeId)
    if (dqe) {
      add(dqe.video)
      setDownloadQueue((prev) => prev.filter((dqe) => dqe.id !== dqeId))
    }
  }

  const hide = (dqeId: number) => {
    setSwiped(dqeId, true)
    setTimeout(() => {
      setDownloadQueue((prev) => prev.map((dqe) => (dqe.id === dqeId ? { ...dqe, hidden: true } : dqe)))
    }, 500)
  }

  const setSwiped = (dqeId: number, swiped: boolean) => {
    setDownloadQueue((prev) => prev.map((dqe) => (dqe.id === dqeId ? { ...dqe, swiped } : dqe)))
  }

  return (
    <QueueContext.Provider value={{ add }}>
      {children}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          right: { xs: 0, sm: 8 },
          width: 400,
          maxWidth: "100%",
          overflow: "hidden",
          zIndex: 1300,
        }}
      >
        {downloadQueue
          .filter((dqe) => !dqe.hidden)
          .map((dqe) => (
            <QueueEntry
              key={dqe.id}
              dqe={dqe}
              cancel={() => cancel(dqe.id)}
              hide={() => hide(dqe.id)}
              retry={() => retry(dqe.id)}
            />
          ))}
      </Box>
    </QueueContext.Provider>
  )
}

interface QueueEntryProps {
  dqe: DownloadQueueElement
  cancel: () => void
  hide: () => void
  retry: () => void
}

const QueueEntry = ({ dqe, cancel, hide, retry }: QueueEntryProps): React.ReactElement => {
  const theme = useTheme()
  return (
    <Grid
      container
      alignItems="center"
      justifyContent="start"
      bgcolor={"primary.main"}
      color={theme.palette.getContrastText(theme.palette.primary.main)}
      p={2}
      position="relative"
      width={"100%"}
      sx={{
        position: "relative",
        overflow: "hidden",
        transform: dqe.swiped ? "translateX(120%)" : "translateX(0)",
        opacity: dqe.swiped ? 0 : 1,
        transition: "transform 0.5s ease-out, opacity 0.5s ease-out",
        "&::before": {
          width: `${dqe.progress}%`,
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          bgcolor: dqe.status === "downloading" ? "blue" : dqe.status === "completed" ? "green" : "red",
          opacity: 0.2,
          content: '""',
          zIndex: 0,
          transition: "width 0.3s ease",
        },
        "& > *": {
          position: "relative",
          zIndex: 1,
        },
      }}
    >
      <Grid size={10}>
        <Typography variant="body1" fontWeight="bold" noWrap textOverflow={"ellipsis"}>
          {`${dqe.progress}% - ${dqe.video.title}`}
        </Typography>
      </Grid>
      <Grid size={2} display={"flex"} flexDirection={"row"} justifyContent={"end"} gap={1}>
        {dqe.status === "error" && (
          <Box
            onClick={retry}
            borderRadius={"50%"}
            height={"25px"}
            width={"25px"}
            p={0.5}
            sx={{
              cursor: "pointer",
              backgroundColor: theme.palette.soft.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RetryIcon width={"auto"} height={"100%"} stroke={theme.palette.mode === "dark" ? "white" : "black"} />
          </Box>
        )}
        {(dqe.status === "completed" || dqe.status === "error") && (
          <Box
            onClick={hide}
            bgcolor={theme.palette.soft.light}
            borderRadius={"50%"}
            height={"25px"}
            width={"25px"}
            p={0.5}
            sx={{
              cursor: "pointer",
              backgroundColor: theme.palette.soft.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CrossIcon width={"auto"} height={"100%"} fill={theme.palette.mode === "dark" ? "white" : "black"} />
          </Box>
        )}
        {dqe.status === "downloading" && (
          <Box
            onClick={cancel}
            bgcolor={theme.palette.soft.light}
            borderRadius={"50%"}
            height={"25px"}
            width={"25px"}
            p={0.5}
            sx={{
              cursor: "pointer",
              backgroundColor: theme.palette.soft.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CrossIcon width={"auto"} height={"100%"} fill={theme.palette.mode === "dark" ? "white" : "black"} />
          </Box>
        )}
      </Grid>
    </Grid>
  )
}
